import { useState } from "react";
import { ArrowRight, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CONTACT, DISCOVERY_SOURCES } from "@/lib/constants";
import { trackEvent } from "@/hooks/useAnalytics";

const PREFILL_MESSAGE =
  "Hi House of Projects, I would like to request an executive conversation about our business.";

/**
 * Discovery Gate Modal - shown before a visitor is handed off to WhatsApp.
 * Asks how they found HOP, then opens a prefilled wa.me chat.
 *
 * Usage:
 *   const [gateOpen, setGateOpen] = useState(false);
 *   <button onClick={() => setGateOpen(true)}>Contact Now</button>
 *   <DiscoveryGateModal open={gateOpen} onOpenChange={setGateOpen} />
 */
export default function DiscoveryGateModal({ open, onOpenChange }) {
  const [selected, setSelected] = useState(null);

  const handleContinue = () => {
    if (!selected) return;

    const sourceLabel = DISCOVERY_SOURCES.find((s) => s.id === selected)?.label || selected;

    // Best-effort anonymous logging via the existing analytics pipe
    // (frontend/src/hooks/useAnalytics.js -> POST /api/analytics).
    // Fails silently if the request doesn't go through - never blocks the CTA.
    trackEvent("discovery_source", { label: sourceLabel, section: "discovery-gate-modal" });

    const url = `${CONTACT.whatsappHref}?text=${encodeURIComponent(PREFILL_MESSAGE)}`;
    window.open(url, "_blank", "noopener,noreferrer");

    setSelected(null);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSelected(null);
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-md bg-white rounded-2xl border-slate-200 p-0 overflow-hidden"
        data-testid="discovery-gate-modal"
      >
        <div className="p-6 md:p-7">
          <DialogHeader className="text-left">
            <div className="w-11 h-11 rounded-full bg-[var(--hop-green)]/10 flex items-center justify-center mb-3">
              <MessageCircle className="w-5 h-5 text-[var(--hop-green)]" />
            </div>
            <DialogTitle className="font-display text-xl md:text-2xl font-bold text-hop-blue tracking-tight">
              Before we chat on WhatsApp
            </DialogTitle>
            <DialogDescription className="text-slate-600 leading-relaxed">
              Quick one - how did you hear about House of Projects?
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 grid grid-cols-2 gap-2.5" data-testid="discovery-gate-options">
            {DISCOVERY_SOURCES.map((option) => {
              const isActive = selected === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelected(option.id)}
                  data-testid={`discovery-gate-option-${option.id}`}
                  aria-pressed={isActive}
                  className={`px-4 py-3 rounded-xl border text-sm font-medium font-display text-left transition ${
                    isActive
                      ? "border-[var(--hop-green)] bg-[var(--hop-green)]/10 text-hop-blue"
                      : "border-slate-200 text-slate-600 hover:border-slate-300 hover:text-hop-blue"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            disabled={!selected}
            onClick={handleContinue}
            data-testid="discovery-gate-continue"
            className={`btn-primary w-full justify-center mt-6 ${
              !selected ? "opacity-40 pointer-events-none" : ""
            }`}
          >
            Continue to WhatsApp <ArrowRight className="arrow w-4 h-4" />
          </button>

          <p className="mt-3 text-xs text-slate-400 text-center">
            Opens WhatsApp at {CONTACT.phoneDisplay}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
